import { App, Button, Card, Skeleton, Space, Tag, Typography } from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  ScheduleOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Link } from 'react-router';
import { hrDepthApi, type LeaveRequest } from '@/api/hr-depth';
import { useAuthStore } from '@/store/auth';
import { useReasonPrompt } from '@/shared/hooks/use-reason-prompt';
import { EmptyState } from '@/shared/components/ui';
import { extractError } from '@/shared/lib/error-message';

/**
 * Pending-approvals card surfaced on the admin/principal dashboard. Lists
 * pending leave requests with inline approve / reject so the Principal
 * doesn't need to navigate into HR → Leave → filter by status to clear the
 * queue.
 *
 * Visibility is gated on `HR_MGMT:APPROVE` because that's the same authority
 * the backend enforces on `/hr/leave/requests/{id}/approve|reject`. Users
 * without the authority see nothing — including the loading shell — so the
 * card stays out of their dashboard entirely.
 *
 * Approve = one click. Reject = prompts for a reason via {@link useReasonPrompt}
 * so the rejection is recorded with context (same pattern used elsewhere for
 * void-receipt and similar destructive flows).
 */
export function PendingApprovalsCard() {
  const canApprove = useAuthStore((s) => s.hasAuthority('HR_MGMT:APPROVE'));
  const qc = useQueryClient();
  const { message } = App.useApp();
  const promptForReason = useReasonPrompt();

  const query = useQuery({
    queryKey: ['hr', 'leave', 'pending'],
    queryFn: () => hrDepthApi.listLeaveRequests({ status: 'PENDING' }),
    enabled: canApprove,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });

  const approve = useMutation({
    mutationFn: (id: number) => hrDepthApi.approveLeaveRequest(id),
    onSuccess: () => {
      message.success('Leave approved');
      qc.invalidateQueries({ queryKey: ['hr', 'leave'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      hrDepthApi.rejectLeaveRequest(id, reason),
    onSuccess: () => {
      message.success('Leave rejected');
      qc.invalidateQueries({ queryKey: ['hr', 'leave'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  // Hide entirely when the user can't approve — no point teasing.
  if (!canApprove) return null;

  return (
    <Card
      title={
        <Space>
          <ScheduleOutlined />
          <span>Pending approvals</span>
          {query.data && query.data.length > 0 && (
            <Tag color="warning">{query.data.length}</Tag>
          )}
        </Space>
      }
      extra={
        <Link to="/hr/leave?tab=requests" style={{ fontSize: 12 }}>
          Open Leave queue →
        </Link>
      }
    >
      {query.isLoading ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : query.data == null || query.data.length === 0 ? (
        <EmptyState
          title="Inbox zero"
          description="No leave requests waiting for approval."
          padded={false}
        />
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {query.data.map((r) => (
            <ApprovalRow
              key={r.id}
              row={r}
              onApprove={() => approve.mutate(r.id)}
              onReject={async () => {
                const reason = await promptForReason({
                  title: `Reject ${r.staffName}'s leave?`,
                  placeholder: 'Reason shared with the requester',
                  danger: true,
                  okText: 'Reject',
                });
                if (reason) reject.mutate({ id: r.id, reason });
              }}
              pending={approve.isPending || reject.isPending}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

interface RowProps {
  row: LeaveRequest;
  onApprove: () => void;
  onReject: () => void;
  pending: boolean;
}

function ApprovalRow({ row, onApprove, onReject, pending }: RowProps) {
  const start = dayjs(row.fromDate);
  const end = dayjs(row.toDate);
  const span =
    start.isSame(end, 'day')
      ? start.format('D MMM')
      : `${start.format('D MMM')} → ${end.format('D MMM')}`;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 10px',
        borderRadius: 6,
        background: 'var(--ant-color-fill-quaternary)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <Typography.Text strong>{row.staffName ?? `Staff #${row.staffId}`}</Typography.Text>
        <Typography.Text type="secondary" style={{ marginInlineStart: 8, fontSize: 12 }}>
          {row.leaveTypeName ?? `Type #${row.leaveTypeId}`} · {row.totalDays} day{Number(row.totalDays) === 1 ? '' : 's'} · {span}
        </Typography.Text>
        {row.reason && (
          <Typography.Paragraph
            type="secondary"
            style={{ fontSize: 12, margin: '2px 0 0' }}
            ellipsis={{ rows: 1, tooltip: row.reason }}
          >
            {row.reason}
          </Typography.Paragraph>
        )}
      </div>
      <Space size={6}>
        <Button
          size="small"
          icon={<CloseOutlined />}
          danger
          onClick={onReject}
          disabled={pending}
        >
          Reject
        </Button>
        <Button
          size="small"
          type="primary"
          icon={<CheckOutlined />}
          onClick={onApprove}
          disabled={pending}
        >
          Approve
        </Button>
      </Space>
    </div>
  );
}
